import { preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import { wrap } from '@lemonade-hq/ts-helpers';
import { isIconProps, isNewLineProps, isTagProps } from '@lemonade-hq/uniclient-chat';
import type { FormattedText, FormattedTextArray } from '@lemonade-hq/uniclient-chat';
import type { SupportedIntrinsicElements, TextProps } from '@lemonade-hq/uniclient-theme';
import { Icon, IsomorphicTooltip, Layout, spacing, Tag, Text } from '@lemonade-hq/uniclient-theme';
import { clsx } from 'clsx';
import type { ForwardedRef } from 'react';
import React, { forwardRef, useMemo } from 'react';
import type { Simplify } from 'type-fest';
import * as styles from './WizardFormattedText.css';

export type FormattedTextItemType = FormattedText | FormattedTextArray;

interface WizardFormattedTextProps {
  readonly text: FormattedTextItemType;
  readonly className?: string;
  readonly wrapperClassName?: string;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
  readonly isLastNode?: boolean;
}

const transformToFormattedTextItems = (text: FormattedTextItemType): FormattedTextArray[] => {
  const wrappedText = wrap(typeof text === 'string' ? { text } : text);

  const result: FormattedTextArray[] = [];

  wrappedText.forEach(item => {
    if ('text' in item && item.text.includes('\n')) {
      const lines = item.text.split('\n');
      lines.forEach((line, index) => {
        if (line) {
          result.push({ ...item, text: line });
        }

        if (index < lines.length - 1) {
          result.push({ type: 'newline' });
        }
      });
    } else {
      result.push(item);
    }
  });

  return result;
};

interface FormattedTextItemProps {
  readonly textItem: FormattedTextArray;
  readonly className?: string;
  readonly color?: string;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
  readonly isLastNode?: boolean;
}

const FormattedTextItem = forwardRef(
  <TElementType extends SupportedIntrinsicElements>(
    {
      textItem,
      className,
      color,
      onAction,
      isLastNode,
      ...restProps
    }: FormattedTextItemProps & Simplify<TextProps<TElementType>>,
    ref: ForwardedRef<JSX.IntrinsicElements[TElementType]>,
  ) => {
    if (isTagProps(textItem)) {
      const { content, ...rest } = textItem;
      return (
        <>
          {isLastNode ? <>&nbsp;</> : null}
          <Tag display="inline-block" key={textItem.content} mx={spacing.s04} {...rest}>
            {content}
          </Tag>
        </>
      );
    }

    if (isNewLineProps(textItem)) {
      return <br key={`newline-${Math.random()}`} />;
    }

    if (isIconProps(textItem)) {
      return (
        <>
          {isLastNode ? <>&nbsp;</> : null}
          <Icon name={textItem.icon} size={textItem.size ?? 'sm'} variant={textItem.variant} />
        </>
      );
    }

    if ('text' in textItem) {
      const { text, tooltip } = textItem;
      const hasTooltip = tooltip != null;
      const tooltipTextStyle = hasTooltip ? 'underline' : undefined;
      const action = 'action' in textItem ? textItem.action : undefined;
      const style = 'style' in textItem ? textItem.style : action ? 'action' : undefined;

      const textComponent = (
        <Text
          as="span"
          className={clsx(styles.formatting({ type: style ?? tooltipTextStyle }), className)}
          color={color}
          key={text}
          type="bodyMedium"
          {...restProps}
          maxWidth="100%"
          onClick={action ? () => void onAction?.(action) : undefined}
          ref={ref}
        >
          {isLastNode ? preventOneWordInLastLine(text, false, false) : text}
        </Text>
      );

      if (hasTooltip) {
        return <IsomorphicTooltip content={tooltip}>{textComponent}</IsomorphicTooltip>;
      }

      return textComponent;
    }
  },
);

const getItemKey = (textItem: FormattedTextArray, index: number): number | string => {
  if (isTagProps(textItem)) {
    return textItem.content;
  }

  if (isNewLineProps(textItem)) {
    return index;
  }

  if (isIconProps(textItem)) {
    return textItem.icon;
  }

  return textItem.text;
};

export const WizardFormattedText = forwardRef(
  <TElementType extends SupportedIntrinsicElements>(
    {
      text,
      wrapperClassName,
      color,
      onAction,
      isLastNode,
      ...restProps
    }: Simplify<TextProps<TElementType> & WizardFormattedTextProps>,
    ref: ForwardedRef<JSX.IntrinsicElements[TElementType]>,
  ): JSX.Element => {
    const textItems = useMemo(() => transformToFormattedTextItems(text), [text]);

    return (
      <Layout className={wrapperClassName} color={color}>
        {textItems.map((textItem, index) => {
          const key = getItemKey(textItem, index);
          const isLastItem = index === textItems.length - 1;
          const addSpace = !isLastItem && !isNewLineProps(textItem);

          return (
            <React.Fragment key={key}>
              <FormattedTextItem
                color={color}
                isLastNode={isLastNode ?? isLastItem}
                onAction={onAction}
                ref={ref}
                textItem={textItem}
                {...restProps}
              />
              {addSpace && <span>&nbsp;</span>}
            </React.Fragment>
          );
        })}
      </Layout>
    );
  },
);
